// Typo-correction suggestion for the email field — "did you mean
// gmail.com?" Never blocks submission (see AuthPage.jsx / StackSocialPage.jsx):
// unusual-but-real domains exist, and refusing a real customer is worse than
// the typo it's trying to catch. Distance-based against a list of common
// providers, not a hand-maintained list of specific misspellings — that list
// always goes stale, edit distance against the real domain doesn't.
const COMMON_DOMAINS = [
  // Global webmail
  'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'icloud.com',
  'live.com', 'msn.com', 'aol.com', 'protonmail.com', 'proton.me',
  'gmx.com', 'mail.com', 'me.com', 'mac.com', 'duck.com', 'yandex.com',
  // Italian ISPs/webmail — this is a mostly-Italian user base
  'libero.it', 'virgilio.it', 'alice.it', 'tiscali.it', 'tin.it',
  'email.it', 'fastwebnet.it', 'hotmail.it', 'yahoo.it', 'outlook.it',
  'live.it', 'gmail.it',
];

// Levenshtein distance, iterative DP — standard implementation, no library.
function editDistance(a, b) {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const prev = new Array(n + 1);
  const curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,      // deletion
        curr[j - 1] + 1,  // insertion
        prev[j - 1] + cost // substitution
      );
    }
    for (let j = 0; j <= n; j++) prev[j] = curr[j];
  }
  return prev[n];
}

// Returns the suggested full email (local-part unchanged, domain corrected)
// or null if the typed domain is already a known domain, too short to
// reason about, or not close enough to any common domain to suggest one.
export function suggestEmailDomain(email) {
  const at = String(email || '').lastIndexOf('@');
  if (at < 0 || at === email.length - 1) return null;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1).trim().toLowerCase();
  if (domain.length < 4) return null;
  if (COMMON_DOMAINS.includes(domain)) return null; // already correct

  let best = null;
  let bestDist = Infinity;
  for (const candidate of COMMON_DOMAINS) {
    const d = editDistance(domain, candidate);
    if (d < bestDist) { bestDist = d; best = candidate; }
  }
  // Distance 1-2 only — anything further apart is likely just a genuinely
  // different, real domain, not a typo of one of these.
  if (best && bestDist >= 1 && bestDist <= 2) {
    return `${local}@${best}`;
  }
  return null;
}
